import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Images } from 'lucide-react';
import { Link } from 'react-router-dom';
import { journey, startYear } from '../data/journey';
export function JourneyPage() {
  const [lightbox, setLightbox] = useState<{
    src: string;
    caption: string;
  } | null>(null);
  return <main className="bg-ivory text-midnight pt-32 pb-24" data-id="element-497">
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 text-center mb-20" data-id="element-498">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-midnight/5 rounded-full text-xs font-bold tracking-widest uppercase mb-6" data-id="element-499">
          <Calendar className="w-4 h-4" data-id="element-500" />
          Our Story
        </div>
        <h1 className="text-5xl md:text-7xl font-display font-bold tracking-tighter leading-[0.95]" data-id="element-501">
          The Journey{' '}
          <span className="font-serif italic font-medium text-gold" data-id="element-502">
            Thus Far
          </span>
        </h1>
        <p className="mt-8 text-lg md:text-xl text-charcoal/70 font-medium max-w-2xl mx-auto leading-relaxed" data-id="element-503">
          From a small gathering in {startYear} to a movement of prayer and
          outreach today — a walk through the years God has carried us.
        </p>
      </section>

      {/* Timeline */}
      <section className="max-w-6xl mx-auto px-6 relative" data-id="element-504">
        {/* Vertical line */}
        <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-midnight/10 -translate-x-1/2 hidden sm:block" data-id="element-505" />

        <div className="space-y-24" data-id="element-506">
          {journey.map((event, idx) => {
          const isLeft = idx % 2 === 0;
          const preview = event.images.slice(0, 2);
          return <motion.div key={event.year} initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6
          }} className="relative" data-id="element-507">
                {/* Year marker */}
                <div className="hidden sm:flex absolute left-6 md:left-1/2 -translate-x-1/2 -top-2 w-12 h-12 rounded-full bg-midnight text-gold items-center justify-center font-display font-bold text-sm shadow-lg ring-4 ring-ivory z-10" data-id="element-508">
                  {String(event.year).slice(-2)}
                </div>

                <div className={`sm:grid sm:grid-cols-2 sm:gap-12 items-start ${isLeft ? '' : 'sm:[&>*:first-child]:order-2'}`} data-id="element-509">
                  {/* Text */}
                  <div className={`pl-20 sm:pl-0 ${isLeft ? 'sm:text-right sm:pr-12' : 'sm:pl-12'}`} data-id="element-510">
                    <div className="inline-block bg-gold text-midnight px-3 py-1 rounded-full text-xs font-bold tracking-widest mb-4" data-id="element-511">
                      {event.year}
                    </div>
                    <h2 className="text-3xl md:text-4xl font-display font-bold tracking-tight mb-4 leading-tight" data-id="element-512">
                      {event.title}
                    </h2>
                    <p className="text-charcoal/70 font-medium leading-relaxed" data-id="element-513">
                      {event.description}
                    </p>
                  </div>

                  {/* Images */}
                  <div className="pl-20 sm:pl-0 mt-8 sm:mt-0" data-id="element-514">
                    <div className="grid grid-cols-2 gap-3" data-id="element-515">
                      {preview.map((img, i) => <button key={i} type="button" onClick={() => setLightbox(img)} className={`group relative overflow-hidden rounded-2xl bg-midnight/5 shadow-sm hover:shadow-xl transition-all duration-500 aspect-square ${i % 2 === 0 ? 'rotate-1 hover:rotate-0' : '-rotate-1 hover:rotate-0'}`} data-id="element-516">
                          <img src={img.src} alt={img.caption} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out" data-id="element-517" />
                          <div className="absolute inset-0 bg-gradient-to-t from-midnight/80 via-midnight/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-518" />
                          <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500" data-id="element-519">
                            <p className="text-ivory text-xs md:text-sm font-serif italic text-left" data-id="element-520">
                              {img.caption}
                            </p>
                          </div>
                        </button>)}
                    </div>

                    {/* View all photos for this year */}
                    <div className={`mt-5 flex ${isLeft ? 'sm:justify-end' : 'sm:justify-start'} justify-start`} data-id="element-521">
                      <Link to={`/gallery#year-${event.year}`} className="group inline-flex items-center gap-2 bg-midnight text-ivory px-5 py-2.5 rounded-full text-sm font-bold tracking-wide hover:bg-gold hover:text-midnight transition-colors" data-id="element-522">
                        <Images className="w-4 h-4" data-id="element-523" />
                        View all {event.year} photos
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </section>

      {/* Closing note */}
      <section className="max-w-3xl mx-auto px-6 mt-32 text-center" data-id="element-524">
        <p className="font-serif italic text-2xl md:text-3xl text-midnight leading-relaxed" data-id="element-525">
          "Hitherto hath the Lord helped us."
        </p>
        <p className="mt-3 text-sm tracking-widest uppercase text-charcoal/60 font-bold" data-id="element-526">
          1 Samuel 7:12
        </p>
        <div className="mt-10" data-id="element-527">
          <Link to="/gallery" className="group inline-flex items-center gap-2 border-2 border-midnight/10 bg-white text-midnight px-7 py-3.5 rounded-full text-sm font-bold tracking-wide hover:border-midnight transition-colors" data-id="element-528">
            <Images className="w-4 h-4" data-id="element-529" />
            Explore the full gallery
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence data-id="element-530">
        {lightbox && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] bg-midnight/95 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setLightbox(null)} role="dialog" aria-modal="true" data-id="element-531">
            <button onClick={() => setLightbox(null)} aria-label="Close" className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-ivory flex items-center justify-center transition-colors" data-id="element-532">
              <X className="w-6 h-6" data-id="element-533" />
            </button>
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="max-w-5xl w-full relative" onClick={e => e.stopPropagation()} data-id="element-534">
              <div className="bg-white p-4 rounded-[2rem] shadow-2xl rotate-1" data-id="element-535">
                <img src={lightbox.src} alt={lightbox.caption} className="w-full max-h-[75vh] object-contain rounded-2xl bg-midnight" data-id="element-536" />
                <p className="text-midnight text-center mt-6 mb-2 font-serif italic text-xl" data-id="element-537">
                  {lightbox.caption}
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </main>;
}